import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook للتمرير إلى أعلى الصفحة عند تغيير المسار
 * يمكن استخدامه في أي مكون لضمان ظهور الصفحة من الأعلى
 */
export const useScrollToTop = (dependencies: any[] = []) => {
  const location = useLocation();

  useEffect(() => {
    // التمرير إلى أعلى الصفحة بشكل سلس
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [location.pathname, ...dependencies]);
};

/**
 * Hook للتمرير إلى أعلى الصفحة فوراً (بدون انتظار تغيير المسار)
 * مفيد عند تحميل بيانات جديدة في نفس الصفحة
 */
export const useScrollToTopImmediate = () => {
  const scrollToTop = (behavior: ScrollBehavior = 'smooth') => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior
    });
  };

  return scrollToTop;
};

/**
 * Hook للتمرير إلى أعلى الصفحة عند تحميل المكون
 * مفيد للصفحات التي تحتاج للظهور من الأعلى دائماً
 */
export const useScrollToTopOnMount = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);
};

/**
 * Hook للتمرير إلى عنصر محدد
 * مفيد للتنقل إلى أقسام معينة في الصفحة
 */
export const useScrollToElement = () => {
  const scrollToElement = (
    elementId: string,
    behavior: ScrollBehavior = 'smooth',
    offset: number = 0
  ) => {
    const element = document.getElementById(elementId);
    if (element) {
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        left: 0,
        behavior
      });
    }
  };

  return scrollToElement;
};

/**
 * Hook للتحقق من موقع التمرير الحالي
 * مفيد لإظهار أزرار "العودة للأعلى" أو تغيير تصميم الهيدر
 */
export const useScrollPosition = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const updateScrollPosition = () => {
      const currentScrollY = window.scrollY;

      setScrollY(currentScrollY);
      setIsScrollingUp(currentScrollY < lastScrollY);
      setIsAtTop(currentScrollY < 10);

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', updateScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrollPosition);
    };
  }, []);

  return {
    scrollY,
    isScrollingUp,
    isAtTop
  };
};

export default {
  useScrollToTop,
  useScrollToTopImmediate,
  useScrollToTopOnMount,
  useScrollToElement,
  useScrollPosition
};
